import { Combobox } from '@base-ui/react/combobox'
import React, { useRef } from 'react'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Close } from 'sutra/components/Icons'
import {
  ChipArea,
  ChipHost,
  ClearButton,
  SearchField,
  Shell,
} from 'sutra/components/MultiSelect/multi-select-styles'
import {
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
  NoResults,
} from 'sutra/components/Select/select-styles'
import type {
  SelectOption,
  SelectValue,
} from 'sutra/components/Select/select-types'
import { Suffix } from 'sutra/components/Select/SelectAdornments'
import { OptionContent } from 'sutra/components/Select/SelectOptionRow'
import {
  type MultiSelectedProps,
  useSelectMulti,
} from 'sutra/components/Select/use-select-multi'

// `multiple` and `tags`: the selection is Chiclets inside the field, with the
// search input inline after them. Base UI's Combobox in `multiple` mode; the
// panel is the same one the single select renders, portaled to `document.body`.
export function SelectMulti<T extends SelectValue, O extends SelectOption<T>>(
  props: MultiSelectedProps<T, O>,
): React.ReactElement {
  const state = useSelectMulti(props)
  const shellRef = useRef<HTMLDivElement>(null)
  const { size = 'sm', zIndex = 1000, panelAlign = 'start', disabled } = props
  const { values } = state
  const shown =
    props.maxTagCount === undefined
      ? values
      : values.slice(0, props.maxTagCount)
  const chipSize = size === 'sm' ? 'sm' : 'md'

  // A press on the field's own chrome (padding, chevron) behaves like a press
  // on the input; one on a chip, its remove button or the input is left alone.
  const handleShellMouseDown = (event: React.MouseEvent): void => {
    if (disabled || (event.target as HTMLElement).closest('button, input')) {
      return
    }
    event.preventDefault()
    ;(shellRef.current?.querySelector('input') as HTMLElement | null)?.focus()
    state.handleOpenChange(!state.isOpen)
  }

  return (
    <Combobox.Root
      multiple
      items={state.items}
      filter={null}
      value={values}
      onValueChange={(next) => state.commit(next)}
      itemToStringLabel={(value) => state.textOf(value)}
      inputValue={state.search.query}
      onInputValueChange={(text, details) =>
        state.handleInput(text, details.reason)
      }
      autoHighlight={props.mode === 'tags'}
      open={state.isOpen}
      onOpenChange={state.handleOpenChange}
      disabled={disabled}
    >
      <Shell
        ref={shellRef}
        $size={size}
        className={props.className}
        style={props.style}
        data-status={props.status}
        data-variant={props.variant}
        data-disabled={disabled || undefined}
        onMouseDown={handleShellMouseDown}
      >
        <ChipArea>
          {shown.map((value) => (
            <ChipHost key={String(value)}>
              <Chiclet
                $size={chipSize}
                onRemove={
                  disabled
                    ? undefined
                    : () => state.commit(values.filter((v) => v !== value))
                }
                removeLabel={`Remove ${state.textOf(value)}`}
              >
                {state.toOption(value).label}
              </Chiclet>
            </ChipHost>
          ))}
          {shown.length < values.length && (
            <Chiclet
              $size={chipSize}
            >{`+${values.length - shown.length}`}</Chiclet>
          )}
          <SearchField
            ref={state.setInputRef}
            id={props.id}
            autoFocus={props.autoFocus}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            aria-busy={props.loading || undefined}
            placeholder={values.length === 0 ? props.placeholder : undefined}
            {...props.domAttributes}
          />
        </ChipArea>
        {props.allowClear && !disabled && values.length > 0 && (
          <ClearButton aria-label="Clear" onClick={props.onClear}>
            <Close width={12} height={12} />
          </ClearButton>
        )}
        <Suffix loading={props.loading} suffixIcon={props.suffixIcon} />
      </Shell>
      <Combobox.Portal>
        <ComboboxPositioner
          anchor={shellRef}
          side="bottom"
          align={panelAlign}
          sideOffset={4}
          $zIndex={zIndex}
        >
          <ComboboxPopup
            className={props.popupClassName}
            $popupWidth={props.popupMatchSelectWidth}
          >
            <NoResults>{state.emptyText}</NoResults>
            <ComboboxList>
              {(value: T) => {
                const option = state.optionByValue.get(value)
                return (
                  <ComboboxItem
                    key={String(value)}
                    value={value}
                    disabled={
                      option?.disabled ||
                      (state.atMax && !values.includes(value))
                    }
                  >
                    {option ? (
                      <OptionContent
                        option={option}
                        optionRender={props.optionRender}
                        withCheck
                      />
                    ) : (
                      `Create "${String(value)}"`
                    )}
                  </ComboboxItem>
                )
              }}
            </ComboboxList>
          </ComboboxPopup>
        </ComboboxPositioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}
